import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useCultivation } from '../contexts/CultivationContext';

const CultivationPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { 
    cultivation, 
    currentLevel, 
    nextLevel, 
    getCultivationProgress, 
    CULTIVATION_LEVELS,
    CULTIVATION_SKILLS,
    practiceSkill,
    ALCHEMY_PILLS
    // 移除了 usePill 的导入，因为我们将通过 context 直接使用
  } = useCultivation();
  
  const [activeTab, setActiveTab] = useState('cultivation');
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [practiceResult, setPracticeResult] = useState(null);
  
  // 如果用户未登录，重定向到登录页面
  if (!user) {
    navigate('/login');
    return null;
  }
  
  const handlePracticeSkill = (skillId) => {
    const result = practiceSkill(skillId);
    if (result) {
      setPracticeResult({
        skillId,
        ...result
      });
      
      // 5秒后清除结果显示
      setTimeout(() => {
        setPracticeResult(null);
      }, 5000);
    }
  };
  
  // 修改为简单功能，不再调用 usePill hook
  const handleUsePill = (pillId) => {
    // 显示一个简单的提示，表示功能尚未完全实现
    alert(`正在炼制${ALCHEMY_PILLS.find(p => p.id === pillId)?.name || '丹药'}，功能即将上线！`);
  };
  
  // 获取技能的经验值
  const getSkillExp = (skillId) => {
    return cultivation.skills[skillId] || 0;
  };
  
  // 获取技能等级（基于经验值）
  const getSkillLevel = (skillId) => {
    const exp = getSkillExp(skillId);
    if (exp < 100) return 1;
    if (exp < 500) return 2;
    if (exp < 1500) return 3;
    if (exp < 5000) return 4;
    if (exp < 15000) return 5;
    return 6;
  };
  
  // 技能经验条百分比
  const getSkillProgressPercent = (skillId) => {
    const exp = getSkillExp(skillId);
    const level = getSkillLevel(skillId);
    const nextLevelExp = level === 1 ? 100 : 
                        level === 2 ? 500 : 
                        level === 3 ? 1500 : 
                        level === 4 ? 5000 : 
                        level === 5 ? 15000 : 50000;
    const prevLevelExp = level === 1 ? 0 : 
                        level === 2 ? 100 : 
                        level === 3 ? 500 : 
                        level === 4 ? 1500 : 
                        level === 5 ? 5000 : 15000;
    
    return Math.round(((exp - prevLevelExp) / (nextLevelExp - prevLevelExp)) * 100);
  };
  
  return (
    <div className="space-y-8">
      {/* 修炼状态头部 */}
      <section className="cyber-panel p-6 rounded-lg bg-gradient-to-r from-cyber-dark to-black">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div>
            <h1 className="text-3xl font-xianxia text-cyber-primary mb-2 cyber-title">修炼系统</h1>
            <p className="text-cyber-light">
              赛博修仙，数据与灵气的完美结合
            </p>
          </div>
          
          <div className="mt-4 md:mt-0 text-right">
            <div className="text-cyber-accent text-2xl font-cyber mb-1">
              {currentLevel?.name || '筑基期'}
            </div>
            <div className="text-cyber-light">
              修为: <span className="text-cyber-secondary">{cultivation.experience}</span>
            </div>
          </div>
        </div>
        
        {nextLevel && (
          <div className="mt-6">
            <div className="flex justify-between mb-1">
              <span className="text-cyber-light">{currentLevel?.name || '筑基期'}</span>
              <span className="text-cyber-light">{nextLevel?.name || ''}</span>
            </div>
            <div className="cultivation-progress">
              <div 
                className="cultivation-progress-bar" 
                style={{ 
                  width: `${getCultivationProgress()}%`,
                  background: `linear-gradient(90deg, var(--${currentLevel?.color}), var(--${nextLevel?.color}))`
                }}
              ></div>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-cyber-light text-sm">
                当前修为: {cultivation.experience}
              </span>
              <span className="text-cyber-light text-sm">
                突破所需: {nextLevel.requiredExp}
              </span>
            </div>
          </div>
        )}
      </section>
      
      {/* 标签导航 */}
      <div className="border-b border-cyber-primary">
        <nav className="flex flex-wrap -mb-px">
          <button
            className={`mr-8 py-2 border-b-2 font-medium text-sm ${
              activeTab === 'cultivation'
                ? 'border-cyber-primary text-cyber-primary'
                : 'border-transparent text-cyber-light hover:text-cyber-secondary hover:border-cyber-secondary'
            }`}
            onClick={() => setActiveTab('cultivation')}
          >
            修炼境界
          </button>
          <button
            className={`mr-8 py-2 border-b-2 font-medium text-sm ${
              activeTab === 'skills'
                ? 'border-cyber-primary text-cyber-primary'
                : 'border-transparent text-cyber-light hover:text-cyber-secondary hover:border-cyber-secondary'
            }`}
            onClick={() => setActiveTab('skills')}
          >
            技能修炼
          </button>
          <button
            className={`mr-8 py-2 border-b-2 font-medium text-sm ${
              activeTab === 'pills'
                ? 'border-cyber-primary text-cyber-primary'
                : 'border-transparent text-cyber-light hover:text-cyber-secondary hover:border-cyber-secondary'
            }`}
            onClick={() => setActiveTab('pills')}
          >
            丹药系统
          </button>
          <button
            className={`mr-8 py-2 border-b-2 font-medium text-sm ${
              activeTab === 'stats'
                ? 'border-cyber-primary text-cyber-primary'
                : 'border-transparent text-cyber-light hover:text-cyber-secondary hover:border-cyber-secondary'
            }`}
            onClick={() => setActiveTab('stats')}
          >
            个人属性
          </button>
        </nav>
      </div>
      
      {/* 标签内容 */}
      <div className="tab-content">
        {/* 修炼境界 */}
        {activeTab === 'cultivation' && (
          <div className="cyber-panel p-6 rounded-lg">
            <h2 className="text-2xl font-cyber text-cyber-primary mb-6">
              修炼境界体系
            </h2>
            <div className="space-y-6">
              <p className="text-cyber-light">
                天道宗修炼体系结合了传统仙侠的境界划分与现代科技数据化的精确测量，
                将修真之路划分为九大境界。每位弟子需通过不断修行积累修为，方能逐步突破。
              </p>
              
              <div className="grid grid-cols-1 gap-4">
                {CULTIVATION_LEVELS.map((level) => (
                  <div 
                    key={level.id} 
                    className={`border rounded-lg p-4 ${
                      level.id === cultivation.level 
                        ? `border-${level.color} bg-${level.color} bg-opacity-10` 
                        : 'border-gray-700'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <h3 className={`text-lg ${level.id === cultivation.level ? `text-${level.color}` : 'text-cyber-light'}`}>
                        {level.id}. {level.name}
                        {level.id === cultivation.level && (
                          <span className="ml-2 text-xs border border-cyber-primary px-2 py-1 rounded-full">
                            当前境界
                          </span>
                        )}
                      </h3>
                      <div className="text-cyber-light text-sm">
                        所需修为: {level.requiredExp}
                      </div>
                    </div>
                    <p className={`mt-2 ${level.id === cultivation.level ? 'text-cyber-light' : 'text-cyber-light text-opacity-70'}`}>
                      {level.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* 技能修炼 */}
        {activeTab === 'skills' && (
          <div className="space-y-6">
            {selectedSkill ? (
              <div className="cyber-panel p-6 rounded-lg">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-cyber text-cyber-primary">
                    {selectedSkill.name}
                  </h2>
                  <button
                    className="cyber-button"
                    onClick={() => setSelectedSkill(null)}
                  >
                    返回技能列表
                  </button>
                </div>
                
                <div className="space-y-6">
                  <p className="text-cyber-light">{selectedSkill.description}</p>
                  
                  <div>
                    <h3 className="text-lg text-cyber-secondary mb-2">技能等级</h3>
                    <div className="flex items-center">
                      <div className="text-3xl text-cyber-accent mr-4">
                        Lv.{getSkillLevel(selectedSkill.id)}
                      </div>
                      <div className="flex-grow">
                        <div className="cultivation-progress">
                          <div 
                            className="cultivation-progress-bar" 
                            style={{ width: `${getSkillProgressPercent(selectedSkill.id)}%` }}
                          ></div>
                        </div>
                        <div className="flex justify-between mt-1">
                          <span className="text-cyber-light text-xs">
                            经验: {getSkillExp(selectedSkill.id)}
                          </span>
                          <span className="text-cyber-light text-xs">
                            下一级: {
                              getSkillLevel(selectedSkill.id) === 1 ? 100 : 
                              getSkillLevel(selectedSkill.id) === 2 ? 500 : 
                              getSkillLevel(selectedSkill.id) === 3 ? 1500 : 
                              getSkillLevel(selectedSkill.id) === 4 ? 5000 : 
                              getSkillLevel(selectedSkill.id) === 5 ? 15000 : 50000
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg text-cyber-secondary mb-2">提升属性</h3>
                    <div className="cyber-panel p-3 inline-block">
                      <span className="text-cyber-light">{selectedSkill.attribute}:</span>
                      <span className="ml-2 text-cyber-accent">+{Math.floor(getSkillLevel(selectedSkill.id) * 1.5)}</span>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg text-cyber-secondary mb-2">修炼方式</h3>
                    <div className="cyber-panel p-4">
                      <p className="text-cyber-light mb-4">
                        点击下方"修炼"按钮开始修炼此技能。每次修炼将获得经验值，提升技能等级和相关属性。
                        {selectedSkill.id === 'cyber_cultivation' && ' 赛博修炼需要在线下实体道场完成，请联系宗门安排。'}
                      </p>
                      
                      {practiceResult && practiceResult.skillId === selectedSkill.id && (
                        <div className="bg-cyber-primary bg-opacity-20 border border-cyber-primary p-3 rounded mb-4">
                          <div className="text-cyber-primary">修炼成功!</div>
                          <div className="text-cyber-light text-sm">获得技能经验: +{practiceResult.expGain.toFixed(1)}</div>
                          <div className="text-cyber-light text-sm">获得修为: +{(practiceResult.expGain / 2).toFixed(1)}</div>
                        </div>
                      )}
                      
                      <button
                        className="cyber-button"
                        onClick={() => handlePracticeSkill(selectedSkill.id)}
                      >
                        修炼
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="cyber-panel p-6 rounded-lg">
                <h2 className="text-2xl font-cyber text-cyber-primary mb-6">
                  技能修炼
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {CULTIVATION_SKILLS.map((skill) => (
                    <div key={skill.id} className="cultivation-item">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg text-cyber-secondary mb-1">
                            {skill.name}
                          </h3>
                          <p className="text-cyber-light text-sm mb-3">
                            {skill.description}
                          </p>
                          <div className="flex items-center">
                            <span className="text-cyber-accent text-sm mr-2">Lv.{getSkillLevel(skill.id)}</span>
                            <div className="w-24 h-2 bg-cyber-dark rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-cyber-accent" 
                                style={{ width: `${getSkillProgressPercent(skill.id)}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                        <button
                          className="cyber-button text-sm"
                          onClick={() => setSelectedSkill(skill)}
                        >
                          详情
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* 丹药系统 */}
        {activeTab === 'pills' && (
          <div className="cyber-panel p-6 rounded-lg">
            <h2 className="text-2xl font-cyber text-cyber-primary mb-6">
              丹药系统
            </h2>
            <div className="space-y-6">
              <p className="text-cyber-light">
                天道宗丹药系统融合了古代炼丹术与现代生物科技，将传统丹药概念数字化，
                提供各种辅助修炼的虚拟丹药，可用功德点兑换。
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {ALCHEMY_PILLS.map((pill) => (
                  <div key={pill.id} className="cyber-panel p-4 rounded">
                    <div className="flex items-center mb-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyber-accent to-cyber-primary mr-4 flex items-center justify-center">
                        <i className="fas fa-pills text-white"></i>
                      </div>
                      <div>
                        <h3 className="text-lg text-cyber-secondary">
                          {pill.name}
                        </h3>
                        <div className="text-cyber-light text-xs">
                          Level {pill.level} • 功德点: {pill.cost}
                        </div>
                      </div>
                    </div>
                    <p className="text-cyber-light text-sm mb-3">
                      {pill.description}
                    </p>
                    <div className="mt-2">
                      <div className="text-sm text-cyber-accent mb-3">效果:</div>
                      <ul className="text-cyber-light text-sm space-y-1">
                        {pill.effect.expMultiplier && (
                          <li>• 经验加成: {(pill.effect.expMultiplier - 1) * 100}%</li>
                        )}
                        {pill.effect.spiritRecovery && (
                          <li>• 灵力恢复: +{pill.effect.spiritRecovery}</li>
                        )}
                        {pill.effect.constitutionBoost && (
                          <li>• 体质提升: +{pill.effect.constitutionBoost}</li>
                        )}
                        {pill.effect.wisdomBoost && (
                          <li>• 智慧提升: +{pill.effect.wisdomBoost}</li>
                        )}
                        {pill.effect.breakthroughChance && (
                          <li>• 突破几率: +{pill.effect.breakthroughChance * 100}%</li>
                        )}
                      </ul>
                    </div>
                    <div className="mt-4">
                      <button
                        className="cyber-button text-sm w-full"
                        onClick={() => handleUsePill(pill.id)}
                      >
                        兑换使用
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {/* 个人属性 */}
        {activeTab === 'stats' && (
          <div className="cyber-panel p-6 rounded-lg">
            <h2 className="text-2xl font-cyber text-cyber-primary mb-6">
              个人属性
            </h2>
            <div className="space-y-6">
              <p className="text-cyber-light">
                属性值反映了你在各方面的能力，影响技能效果和修炼速度。
                通过修炼不同的技能，可以提升相应的属性值。
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="cyber-panel p-4 rounded">
                  <h3 className="text-lg text-cyber-secondary mb-3">体质</h3>
                  <div className="text-4xl text-cyber-accent mb-2">
                    {cultivation.attributes.constitution}
                  </div>
                  <p className="text-cyber-light text-sm">
                    影响生命力、耐力和身体强健程度
                  </p>
                </div>
                
                <div className="cyber-panel p-4 rounded">
                  <h3 className="text-lg text-cyber-secondary mb-3">精神</h3>
                  <div className="text-4xl text-cyber-accent mb-2">
                    {cultivation.attributes.spirit}
                  </div>
                  <p className="text-cyber-light text-sm">
                    影响灵力总量、灵力恢复速度和精神力量
                  </p>
                </div>
                
                <div className="cyber-panel p-4 rounded">
                  <h3 className="text-lg text-cyber-secondary mb-3">控制</h3>
                  <div className="text-4xl text-cyber-accent mb-2">
                    {cultivation.attributes.control}
                  </div>
                  <p className="text-cyber-light text-sm">
                    影响灵力操控精度、技能施放效率
                  </p>
                </div>
                
                <div className="cyber-panel p-4 rounded">
                  <h3 className="text-lg text-cyber-secondary mb-3">力量</h3>
                  <div className="text-4xl text-cyber-accent mb-2">
                    {cultivation.attributes.strength}
                  </div>
                  <p className="text-cyber-light text-sm">
                    影响物理攻击力、负重能力
                  </p>
                </div>
                
                <div className="cyber-panel p-4 rounded">
                  <h3 className="text-lg text-cyber-secondary mb-3">智力</h3>
                  <div className="text-4xl text-cyber-accent mb-2">
                    {cultivation.attributes.intelligence}
                  </div>
                  <p className="text-cyber-light text-sm">
                    影响学习速度、记忆力和灵力技能威力
                  </p>
                </div>
                
                <div className="cyber-panel p-4 rounded">
                  <h3 className="text-lg text-cyber-secondary mb-3">敏捷</h3>
                  <div className="text-4xl text-cyber-accent mb-2">
                    {cultivation.attributes.dexterity}
                  </div>
                  <p className="text-cyber-light text-sm">
                    影响反应速度、手部精细动作能力
                  </p>
                </div>
                
                <div className="cyber-panel p-4 rounded">
                  <h3 className="text-lg text-cyber-secondary mb-3">感知</h3>
                  <div className="text-4xl text-cyber-accent mb-2">
                    {cultivation.attributes.perception}
                  </div>
                  <p className="text-cyber-light text-sm">
                    影响对灵气的感知、察觉危险能力
                  </p>
                </div>
                
                <div className="cyber-panel p-4 rounded">
                  <h3 className="text-lg text-cyber-secondary mb-3">智慧</h3>
                  <div className="text-4xl text-cyber-accent mb-2">
                    {cultivation.attributes.wisdom}
                  </div>
                  <p className="text-cyber-light text-sm">
                    影响决策能力、领悟能力和修炼领悟
                  </p>
                </div>
                
                <div className="cyber-panel p-4 rounded">
                  <h3 className="text-lg text-cyber-secondary mb-3">科技</h3>
                  <div className="text-4xl text-cyber-accent mb-2">
                    {cultivation.attributes.technology}
                  </div>
                  <p className="text-cyber-light text-sm">
                    影响对现代科技的理解、赛博修炼能力
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CultivationPage;
